import React from 'react';

import styled from 'styled-components';

interface ReportDetailsProps {
  reportData: {
    store_id: string | null;
    date: string | null;
    amount: number;
    refunds: number;
    fees: number;
  }[];
}

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  color: #333;
`;

const Card = styled.div`
  padding: 1.5rem;
  background: #fff;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  text-align: left;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  display: none;

  @media (min-width: 768px) {
    display: table-header-group;
    background: #f3f3f3;
  }
`;

const Th = styled.th`
  padding: 0.5rem 1rem;
`;

const Tbody = styled.tbody``;

const Row = styled.tr`
  border-bottom: 1px solid #eaeaea;

  &.total {
    border-top: 2px solid #333;
    font-weight: bold;
  }

  @media (min-width: 768px) {
    &.mobile-only {
      display: none;
    }
  }
`;

const Cell = styled.td`
  padding: 0.5rem 1rem;

  &.text-right {
    text-align: right;
  }
`;

const MobileRow = styled.tr`
  display: flex;
  flex-direction: column;
  border-bottom: 2px solid #eaeaea;

  &.last {
    border-bottom: none;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileCell = styled.div`
  padding: 0.5rem 1rem;

  & + & {
    margin-top: 0.5rem;
  }

  display: flex;
  justify-content: space-between;

  span:first-child {
    font-weight: bold;
  }
`;

const ReportDetails: React.FC<ReportDetailsProps> = ({ reportData }) => {
  const dailyReports = reportData
    .filter((report) => report.date)
    .filter((report) => !report.store_id)
    .sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime());

  const totalReport = reportData.find((report) => !report.date);

  return (
    <Container>
      <Card>
        <Table>
          <Thead>
            <tr>
              <Th>Date</Th>
              <Th className="text-right">Amount</Th>
              <Th className="text-right">Refunds</Th>
              <Th className="text-right">After Refunds</Th>
              <Th className="text-right">Fees</Th>
              <Th className="text-right">Net Result</Th>
            </tr>
          </Thead>
          <Tbody>
            {dailyReports.map((report, index) => (
              <>
                <Row key={index}>
                  <Cell>{new Date(report.date!).toLocaleDateString()}</Cell>
                  <Cell className="text-right">{(report.amount / 100).toLocaleString()}</Cell>
                  <Cell className="text-right">{(report.refunds / 100).toLocaleString()}</Cell>
                  <Cell className="text-right">
                    {((report.amount - report.refunds) / 100).toLocaleString()}
                  </Cell>
                  <Cell className="text-right">{(report.fees / 100).toLocaleString()}</Cell>
                  <Cell className="text-right">
                    {((report.amount - report.refunds - report.fees) / 100).toLocaleString()}
                  </Cell>
                </Row>
                <MobileRow className={dailyReports.length - 1 === index ? 'last' : ''}>
                  <MobileCell>
                    <span>Date:</span>
                    <span>{new Date(report.date!).toLocaleDateString()}</span>
                  </MobileCell>
                  <MobileCell>
                    <span>Amount:</span>
                    <span>{(report.amount / 100).toLocaleString()}</span>
                  </MobileCell>
                  <MobileCell>
                    <span>Refunds:</span>
                    <span>{(report.refunds / 100).toLocaleString()}</span>
                  </MobileCell>
                  <MobileCell>
                    <span>After Refunds:</span>
                    <span>{((report.amount - report.refunds) / 100).toLocaleString()}</span>
                  </MobileCell>
                  <MobileCell>
                    <span>Fees:</span>
                    <span>{(report.fees / 100).toLocaleString()}</span>
                  </MobileCell>
                  <MobileCell>
                    <span>Net Result:</span>
                    <span>
                      {((report.amount - report.refunds - report.fees) / 100).toLocaleString()}
                    </span>
                  </MobileCell>
                </MobileRow>
              </>
            ))}
            {totalReport && (
              <Row className="total">
                <Cell>Total</Cell>
                <Cell className="text-right">{(totalReport.amount / 100).toLocaleString()}</Cell>
                <Cell className="text-right">{(totalReport.refunds / 100).toLocaleString()}</Cell>
                <Cell className="text-right">
                  {((totalReport.amount! - totalReport.refunds!) / 100).toLocaleString()}
                </Cell>
                <Cell className="text-right">{(totalReport.fees / 100).toLocaleString()}</Cell>
                <Cell className="text-right">
                  {(
                    (totalReport.amount! - totalReport.refunds! - totalReport.fees!) /
                    100
                  ).toLocaleString()}
                </Cell>
              </Row>
            )}
          </Tbody>
        </Table>
      </Card>
    </Container>
  );
};

export default ReportDetails;
